import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars

import {Gif, MEDIA_QUALITY} from './Gif';
import {GifOverlay} from './GifCommon';
import {TrendsTag} from './Tag';
import {CustomComponent, Link} from '../../common/components';
import {ProgressCircleIndeterminate} from '../../common/components/ProgressCircle';
import {getGifEventParams, getPageType, isSticker, isTouchDevice} from '../../common/util';
import {GifFavButton} from '../components/GifFavButton';
import {UniversalGifListItem} from '../components/UniversalGifList';
import {ShareCount} from '../components/ShareCount';
import type {Input as HammerInput} from 'hammerjs';

import {subscribe} from '../../../replete';

import './Carousel.scss';

function getClientDims(event: MouseEvent|TouchEvent): [number, number]|void {
    const typeStart = event.type.slice(0, 5);
    if (typeStart === 'mouse') {
        event = event as MouseEvent;
        return [event.clientX, event.clientY];
    } else if (typeStart === 'touch') {
        event = event as TouchEvent;
        return [event.touches[0].clientX, event.touches[0].clientY];
    }
}

export class CarouselItem extends UniversalGifListItem {
    static debugNamespace = 'CarouselItem';

    element: HTMLElement|null;
    height?: number;
    width?: number;

    @autobind
    setElement(element) {
        this.element = element;
        const {height} = this.props;
        if (element) {
            this.height = height || element.offsetWidth;
            this.width = this.height;
            this.triggerUpdate();
        }
    }

    @autobind
    onClick() {
        this.props.trackingCallback({idx: this.props.idx, gif: this.props.gif});
        this.reuseGifData();
    }

    render() {
        const {gif, margin, height} = this.props;
        const isClickDisabled = this.props.disableOnClick || !gif.itemurl;
        const itemStyle: app.Style = {};
        if (margin) {
            itemStyle.margin = `0 ${margin}px`;
        }
        if (height) {
            itemStyle.height = `${height}px`;
            itemStyle.width = `${height}px`;
        }

        return (
            <figure
                className="UniversalGifListItem"
                ref={this.setElement}
                style={itemStyle}
            >
                <Link
                    to={isClickDisabled ? undefined : this.linkToView(gif)}
                    onClick={isClickDisabled ? undefined : this.onClick}
                >
                    <Gif
                        inList={true}
                        gif={gif}
                        quality={MEDIA_QUALITY.TINYWEBP}
                        width={this.width}
                        height={this.height}
                    />
                    {gif.ad_badge_info?.badges.length && <img
                        className={
                            `badge pos-${gif.ad_badge_info.badges[0].position}`}
                        src={gif.ad_badge_info.badges[0].url}
                        width={gif.ad_badge_info.badges[0].dims[0] / 2}
                        height={gif.ad_badge_info.badges[0].dims[1] / 2}
                    />}
                    <div className="overlay" />
                </Link>
                { !isSticker(gif) &&
                    <div className="actions">
                        <GifFavButton gif={gif} />
                    </div>
                }
                <figcaption className="tags">
                    {this.makeTagAnchors(gif.tags)}
                </figcaption>
                <GifOverlay gif={gif}/>
                {this.props.showShareCount && <ShareCount gif={gif}/>}
            </figure>
        );
    }
}

class BaseCarousel extends CustomComponent {
    static debugNamespace = 'BaseCarousel';

    carouselEl: HTMLElement|null;
    framesContainerEl: HTMLElement|null;
    itemsPerPage: number;
    ITEM_HEIGHT: number;
    ITEM_MARGIN: number;

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        this.carouselEl = null;
        this.framesContainerEl = null;
        this.itemsPerPage = 0;
        this.ITEM_HEIGHT = props.height;
        this.ITEM_MARGIN = props.margin;
        this.state = {
            ...this.state,
            fetchingMoreItems: false,
            scrollPos: 0,
            swipeDistance: 0,
        };
    }
    componentDidMount() {
        window.addEventListener('resize', this.updateCarouselDimensions);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateCarouselDimensions);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.items.length !== this.props.items.length) {
            this.setState({fetchingMoreItems: false});
            // NOTE: allows setState to complete before updating dimensions
            setTimeout(this.updateCarouselDimensions, 10);
        }
    }

    @autobind
    setCarouselEl(element: HTMLElement|null) {
        this.carouselEl = element;
        this.updateCarouselDimensions();
        this.triggerUpdate();
    }

    getCarouselElOffsetWidth() {
        return this.carouselEl?.offsetWidth || 0;
    }

    updateCarouselDimensions() {}

    @autobind
    setFramesContainerEl(element) {
        this.framesContainerEl = element;
    }

    fetchMoreItems() {
        if (this.state.fetchingMoreItems ||
            !this.props.itemsExhaustedCallback) {
            return;
        }
        this.props.itemsExhaustedCallback();
        this.setState({fetchingMoreItems: true});
    }

    @autobind
    trackItemSelect({idx, gif}: {idx: number, gif: api.Post}) {
        const eventName = 'stickers_carousel_result_tap';
        const params = getGifEventParams(this.context, idx, gif);

        this.context.apiService.registerEvent(eventName, params);
    }

    @autobind
    trackCarouselMove(type: string, direction: string, page?: number) {
        let eventName;
        if (direction === 'next') {
            eventName = 'stickers_carousel_advance';
        } else if (direction === 'prev') {
            eventName = 'stickers_carousel_retreat';
        }

        const params: app.URLParamsObj = {};
        if (type === 'swipe') {
            const carouselPosition = this.state.scrollPos +
                this.state.swipeDistance + this.getCarouselElOffsetWidth();
            const stickerWidth = (this.ITEM_HEIGHT + this.ITEM_MARGIN * 2);
            params.viewindex = (
                Math.floor(carouselPosition / stickerWidth) - 1).toString();
        } else if (type === 'button') {
            params.viewindex = (
                (page + 1) * this.itemsPerPage - 1).toString();
        }

        params.category = getPageType(this.context);

        this.context.apiService.registerEvent(eventName, params);
    }
}


export class MobileCarousel extends BaseCarousel {
    static debugNamespace = 'MobileCarousel';

    carouselBottomPadding: number;
    momentumTimeout: number;
    momentumMaxSpeed: number;
    isTouchDevice: boolean;
    isMomentumScrolling?: boolean;
    touchInitiated?: boolean;
    hasMoved?: boolean;
    momentumScrollStartTime?: number|null;
    scrollMax?: number;
    scrollDirection?: string;
    clientX?: number;
    clientY?: number;
    direction?: string;
    momentumSpeed?: number;
    prevStepTime?: number;

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        this.state = {
            ...this.state,
            scrollPos: 0,
            swipeDistance: 0,
            clientX: 0,
            fetchingMoreItems: false,
        };
        this.carouselBottomPadding = 20;
        this.momentumTimeout = 1000;
        this.momentumMaxSpeed = 2;
        this.isTouchDevice = isTouchDevice();
    }

    componentDidMount() {
        super.componentDidMount();
        if (process.env.BROWSER) {
            // NOTE: need to dynamically import hammerjs since it requires
            // the window object (will throw an Error if imported server-side)
            import('hammerjs').then((hammer) => {
                if (this.framesContainerEl) {
                    const Hammer = hammer.default;
                    const hammertime = new Hammer(this.framesContainerEl);
                    const options = {
                        threshold: 10,
                        velocity: .5,
                        direction: Hammer.DIRECTION_HORIZONTAL,
                    };
                    hammertime.get('swipe').set(options);
                    hammertime.on('swipe', this.startMomentumScroll);
                }
            });
        }
    }

    @autobind
    handleTouchStart(e: MouseEvent|TouchEvent) {
        e.preventDefault();
        e.stopPropagation();
        this.preventWindowScrolling(true);
        this.isMomentumScrolling && this.stopMomentumScroll();
        this.touchInitiated = true;
        this.hasMoved = false;
        const dims = getClientDims(e);
        if (dims) {
            [this.clientX, this.clientY] = dims;
        }
    }

    @autobind
    handleTouchEnd(e: MouseEvent|TouchEvent) {
        this.scrollDirection = undefined;
        this.stopMovementTracking();
    }

    @autobind
    stopMovementTracking() {
        this.preventWindowScrolling(false);

        !this.isMomentumScrolling && this.setFinalScrollPositionState();
    }

    setFinalScrollPositionState() {
        const {scrollPos, swipeDistance} = this.state;
        swipeDistance > 0 && this.trackCarouselMove('swipe', 'next');
        swipeDistance < 0 && this.trackCarouselMove('swipe', 'prev');

        const newScrollPos = scrollPos + swipeDistance;
        this.setState({
            scrollPos: newScrollPos >= 0 ? newScrollPos : 0,
            swipeDistance: 0,
        });
    }

    preventWindowScrolling(bool: boolean) {
        const document = window.document;
        if (bool) {
            document.documentElement.classList.add('no-scroll-maintain-y-offset');
            document.body.classList.add('no-scroll-maintain-y-offset');
        } else {
            document.documentElement.classList.remove('no-scroll-maintain-y-offset');
            document.body.classList.remove('no-scroll-maintain-y-offset');
        }
    }

    @autobind
    startMomentumScroll(e: HammerInput) {
        // NOTE: hammerjs swipe events on Android sometimes incorrectly register
        // velocity as zero. We should use overallVelocity as a fallback.
        const velocityX = e.velocityX !== 0 ? e.velocityX : e.overallVelocityX;
        this.direction = velocityX > 0 ? 'right' : 'left';
        const speed = Math.abs(velocityX);
        if (speed === 0) {
            this.stopMomentumScroll();
        } else {
            this.momentumSpeed = Math.min(speed, this.momentumMaxSpeed);
            this.isMomentumScrolling = true;
            window.requestAnimationFrame(this.momentumScrollStep);
        }
    }

    @autobind
    stopMomentumScroll(e?: HammerInput) {
        this.isMomentumScrolling = false;
        this.momentumScrollStartTime = null;
        this.setFinalScrollPositionState();
    }

    @autobind
    momentumScrollStep(timestamp: number) {
        if (!this.momentumScrollStartTime) {
            this.momentumScrollStartTime = timestamp;
            this.prevStepTime = timestamp;
        }
        const elapsed = timestamp - this.prevStepTime;
        const totalElapsed = timestamp - this.momentumScrollStartTime;
        this.prevStepTime = timestamp;

        // approximates ease-out
        const timingFn = Math.pow(
            (this.momentumTimeout - totalElapsed) / this.momentumTimeout, 2);
        const offset = Math.ceil(this.momentumSpeed * elapsed * timingFn) *
            (this.direction === 'left' ? 1 : -1);

        const newSwipeDistance = Math.round(this.state.swipeDistance + offset);
        const newPos = this.state.scrollPos + newSwipeDistance;

        const isOutOfBounds = newPos <= 0 || newPos >= this.scrollMax;
        if (isOutOfBounds || totalElapsed > this.momentumTimeout ||
            !this.isMomentumScrolling) {
            this.stopMomentumScroll();
            newPos >= this.scrollMax && this.fetchMoreItems();
        } else {
            this.setState({swipeDistance: newSwipeDistance});
            window.requestAnimationFrame(this.momentumScrollStep);
        }
    }

    @autobind
    move(e: MouseEvent|TouchEvent) {
        let clientX;
        let clientY;
        const dims = getClientDims(e);
        if (dims) {
            [clientX, clientY] = dims;
        }

        const moveX = this.clientX - clientX;
        if (this.touchInitiated) {
            this.touchInitiated = false;
            this.hasMoved = true;
            const moveY = this.clientY - clientY;
            this.scrollDirection = Math.abs(moveY) > Math.abs(moveX) ?
                'vertical' : 'horizontal';
        }

        if (this.scrollDirection === 'horizontal') {
            const currentScrollPos = moveX + this.state.scrollPos;
            if (currentScrollPos > this.scrollMax) {
                this.fetchMoreItems();
            } else if (currentScrollPos >= 0) {
                this.setState({swipeDistance: moveX});
            }
        } else if (this.scrollDirection === 'vertical') {
            this.stopMovementTracking();
        }
    }

    @autobind
    updateCarouselDimensions() {
        if (this.framesContainerEl && this.carouselEl) {
            const carouselWidth = this.getCarouselElOffsetWidth();
            const framesContainerWidth = this.framesContainerEl.offsetWidth;
            this.scrollMax = framesContainerWidth - carouselWidth +
                this.ITEM_HEIGHT + (this.ITEM_MARGIN * 2);
        } else {
            this.scrollMax = 0;
        }
    }

    render() {
        const {items} = this.props;
        const {scrollPos, swipeDistance} = this.state;
        let carouselShift = scrollPos + swipeDistance;
        carouselShift = carouselShift < 0 ? 0 : carouselShift;
        carouselShift = carouselShift > this.scrollMax ?
            this.scrollMax : carouselShift;

        let itemList = [];
        itemList = items.map((item: api.Post, index: number) => {
            return (
                <CarouselItem
                    gif={item}
                    idx={index}
                    key={index}
                    height={this.ITEM_HEIGHT}
                    margin={this.ITEM_MARGIN}
                    trackingCallback={this.trackItemSelect}
                />
            );
        });
        if (this.state.fetchingMoreItems) {
            itemList.push(
                <div
                    className='loader'
                    style={{
                        height: `${this.ITEM_HEIGHT}px`,
                        width: `${this.ITEM_HEIGHT}px`,
                    }}
                >
                    <ProgressCircleIndeterminate
                        diameter={34}
                        strokeWidthRatio={.1}
                        color={'#007add'}
                        animationDuration={2000}
                    />
                </div>,
            );
        }

        return (
            <div
                className="MobileCarousel"
                onTouchStart={this.handleTouchStart}
                onTouchEnd={this.handleTouchEnd}
                onTouchMove={this.move}
                onMouseDown={this.isTouchDevice ?
                    undefined : this.handleTouchStart}
                onMouseUp={this.isTouchDevice ?
                    undefined : this.handleTouchEnd}
                onMouseMove={this.isTouchDevice ? undefined : this.move}
            >
                <div
                    className={`carousel-container itemList${this.props.showShareCount ? ' showShareCount' : ''}`}
                    ref={this.setCarouselEl}
                    style={{height: `${
                        this.ITEM_HEIGHT + this.carouselBottomPadding}px`}}
                >
                    <div
                        className="frames-container"
                        ref={this.setFramesContainerEl}
                        style={{transform: `translate(${-carouselShift}px)`}}
                    >
                        {itemList}
                    </div>
                </div>
            </div>
        );
    }
}

@subscribe({
    isMobile: ['ui.isMobile'],
})
export class Carousel extends BaseCarousel {
    static debugNamespace = 'Carousel';

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        this.state = {
            ...this.state,
            scrollPos: 0,
            page: 0,
            maxPage: 0,
        };
        this.ITEM_MARGIN = 10;
        this.updateCarouselDimensions();
    }

    @autobind
    onNext() {
        if (this.state.page >= this.state.maxPage) {
            return;
        }
        const carouselWidth = this.getCarouselElOffsetWidth();
        const page = this.state.page + 1;
        this.trackCarouselMove('button', 'next', page);
        page === this.state.maxPage && this.fetchMoreItems();

        this.setState({
            page,
            scrollPos: page * (carouselWidth),
        });
    }

    @autobind
    onPrev() {
        if (this.state.page <= 0) {
            return;
        }
        const carouselWidth = this.getCarouselElOffsetWidth();
        const page = this.state.page - 1;
        this.trackCarouselMove('button', 'prev', page);
        this.setState({
            page,
            scrollPos: page * carouselWidth,
        });
    }

    @autobind
    updateCarouselDimensions() {
        const {type, items} = this.props;
        const numItems = items.length;
        this.itemsPerPage = 5;

        if (this.carouselEl) {
            const carouselWidth = this.getCarouselElOffsetWidth();

            if (type === 'tags' && carouselWidth <= 930) {
                this.itemsPerPage = 4;
            }

            if (type === 'stickers') {
                if (carouselWidth <= 812) {
                    this.itemsPerPage = 4;
                }
                if (carouselWidth <= 600) {
                    this.itemsPerPage = 3;
                }
            }
        }
        const maxPage = Math.ceil(numItems / this.itemsPerPage) - 1;

        if (this.state.maxPage !== maxPage) {
            this.setState({maxPage});
        }
    }

    render() {
        const {items, type, isMobile} = this.props;
        const {scrollPos, page, maxPage} = this.state;

        if (type === 'items') {
            const itemList = items.map((item: api.Post, index: number) => {
                return (
                    <CarouselItem
                        gif={item}
                        idx={index}
                        key={index}
                        margin={this.ITEM_MARGIN}
                        trackingCallback={this.trackItemSelect}
                        showShareCount={this.props.showShareCount}
                    />
                );
            });

            return (
                <div className="Carousel items">
                    <div
                        className={`carousel-container itemList${this.props.showShareCount ? ' showShareCount' : ''}`}
                        ref={this.setCarouselEl}
                    >
                        <div
                            className="frames-container"
                            ref={this.setFramesContainerEl}
                            style={{left: `-${scrollPos}px`}}
                        >
                            {itemList}
                        </div>
                    </div>
                    <div className={`buttons${maxPage ? '' : ' hidden'}`}>
                        <div
                            className={`prev-button scroll-button${page > 0 ? '' : ' disabled'}`}
                            onClick={this.onPrev}
                        >
                            <i className="iconfont-chevron-left-icon" />
                        </div>
                        <div
                            className={`next-button scroll-button${page < maxPage ? '' : ' disabled'}`}
                            onClick={this.onNext}
                        >
                            <i className="iconfont-chevron-right-icon" />
                        </div>
                    </div>
                </div>
            );
        } else if (type === 'tags') {
            const itemList = items.map((item: api.Category) => {
                return <TrendsTag
                    tag={item}
                    key={item.searchterm}
                    onClick={() => {
                        this.context.apiService.registerEvent('home_trending_category_tap', {
                            tag: item.searchterm,
                        });
                    }}
                />;
            });

            if (isMobile) {
                return (
                    <div className="Carousel tagList">
                        {itemList}
                    </div>
                );
            }
            return (
                <div className="Carousel tags">
                    <div
                        className={`carousel-container tagList`}
                        ref={this.setCarouselEl}
                    >
                        <div
                            className="frames-container"
                            ref={this.setFramesContainerEl}
                            style={{left: `-${scrollPos}px`}}
                        >
                            {itemList}
                        </div>
                    </div>
                    {page > 0 && <div
                        className={`prev-button scroll-button`}
                        onClick={this.onPrev}
                    >
                        <i className="iconfont-chevron-left-icon" />
                    </div>}
                    {page < maxPage && <div
                        className="next-button scroll-button"
                        onClick={this.onNext}
                    >
                        <i className="iconfont-chevron-right-icon" />
                    </div>}
                </div>
            );
        }
    }
}
